import { Fragment, useState } from 'react';
import {
  facilityDevices,
  integrationAvailability,
  integrationCatalog,
  integrationCreate,
  integrationRemove,
  integrations as integrationsRoute,
  integrationTest,
  webhookRouteCreate,
  webhookRouteToggle,
  webhookRoutes,
  WEBHOOK_INBOUND_PREFIX,
  type ApiError,
  type IntegrationCatalogItem,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { DEVICE_CATALOG } from '../../content/deviceCatalog';
import { ErrLine, I, btn, chip, pill, pre, sx, useApi, useGuarded, usePage } from './kit';
import { logoBg } from './logos';
import { OutboundAlerts } from './OutboundAlerts';
import { useConsoleRole } from '../../session/SessionContext';

type Tab = 'Inbound' | 'Outbound' | 'Connect stack' | 'Wearables';

const STATE_WORD: Record<string, string> = { live: 'LIVE', partner_pending: 'PARTNER-PENDING', coming_soon: 'NOT STARTED' };
const PROTOCOL: Record<string, string> = { FHIR_R4: 'FHIR R4', REST_API: 'REST API', WEBHOOK: 'Webhook', SDK: 'SDK' };
/** The vendor mark on file for a catalog system (design STACK logos). */
const STACK_LOGO: Record<string, string> = {
  cerner: 'oraclehealth.png', athenahealth: 'athenahealth-trim.png', quest: 'quest-trim.png', labcorp: 'labcorp-trim.png', 'health-gorilla': 'healthgorilla.png',
  particle: 'particlehealth.png', pointclickcare: 'pointclickcare-trim.png', matrixcare: 'matrixcare.png', epic: 'epic.png', redox: 'redox.png',
};
const INI: Record<string, string> = { 'Oracle Health (Cerner)': 'OH', athenahealth: 'ah', 'Quest Diagnostics': 'Q', Labcorp: 'Lc', 'Health Gorilla': 'HG', 'Particle Health': 'PH', Redox: 'Rx' };
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
/** Designed device rows whose catalog entry has another name (the watch syncs through the phone's health store). */
const DEVICE_CATALOG_ID: Record<string, string> = { 'Apple Watch': 'apple-health', 'Samsung Galaxy Watch': 'samsung-health' };
const NOT_IN_CATALOG = 'This device is not in the integration catalog yet, so it cannot be set up here.';
/** Why a device that is set up is still not live, by how it connects (plain, one line). */
function notLiveWhy(item: IntegrationCatalogItem): string {
  if (item.protocol === 'SDK') return 'Residents connect from the phone app.';
  if (item.directOAuth) return 'Add its client ID and secret to go live.';
  if (item.protocol === 'WEBHOOK') return 'Waiting on Aithora to switch it on.';
  return 'Resident sign-in is not built yet.';
}

/** Integrations (design seIsIntegrations, T9065; L21921-21957 and L22489-22624). */
/** What Test connection says: reached, checked per resident (a direct wearable, not a failure), or not reached. */
function testLine(r: { ok: boolean; latencyMs: number; error?: string | null }): string {
  if (r.ok) return 'Reached · ' + Math.round(r.latencyMs) + 'ms';
  if (r.error && / is checked per resident: /.test(r.error)) return r.error;
  return 'Not reached · ' + (r.error ?? 'no answer');
}

export function IntegrationsSection() {
  const { client } = useSession();
  const { facilityId, ask, changed } = usePage();
  const { canWrite, roReason, wr } = useGuarded();
  const { role } = useConsoleRole();
  const operatorTier = role === 'SUPER_ADMIN' || role === 'OPERATOR';
  const [tab, setTab] = useState<Tab>('Wearables');
  const [admin, setAdmin] = useState(false);
  // The catalog is platform-wide ("hides an integration from every facility"): the server
  // lets only Aithora's operator tier change it (govern.ts, isPlatformStaff), so only they manage it.
  const manage = admin && operatorTier;
  const [howOpen, setHowOpen] = useState(false);
  const [connect, setConnect] = useState<IntegrationCatalogItem | null>(null);
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [hook, setHook] = useState<{ name: string; url: string } | null>(null);
  const [hookAdded, setHookAdded] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [copiedHook, setCopiedHook] = useState<string | null>(null);
  const [tested, setTested] = useState<Record<string, string>>({});
  // A Set up that cannot go ahead says why next to the control that was clicked.
  const [placeErr, setPlaceErr] = useState<{ where: string; error: ApiError } | null>(null);

  const catalog = useApi((c, s) => integrationCatalog(c, { signal: s }), []);
  const mine = useApi((c, s) => integrationsRoute(c, { signal: s }), [facilityId], !!facilityId);
  const devices = useApi((c, s) => facilityDevices(c, { params: { id: 'me' }, signal: s }), [facilityId], !!facilityId);
  // Webhook routes are Aithora's tenant settings: the server answers the operator tier only
  // (requireTenantSettingsWriter), so no other role asks for them.
  const hooks = useApi((c, s) => webhookRoutes(c, { signal: s }), [], operatorTier);

  const all = Object.values(catalog.data?.catalog ?? {}).flat();
  const stack = all.filter((x) => x.category !== 'WEARABLE');
  const findItem = (name: string) => all.find((x) => norm(x.name) === norm(name) || norm(x.name).startsWith(norm(name)) || norm(name).startsWith(norm(x.name)));
  const connectedOf = (name: string) => (mine.data?.integrations ?? []).find((x) => norm(x.name) === norm(name) && x.configured);
  const deviceSetUp = (name: string) => {
    const d = (devices.data?.devices ?? []).find((x) => norm(x.name) === norm(name) || norm(x.name).startsWith(norm(name)));
    return !!d?.configured || !!connectedOf(name);
  };
  const noBuilding = !facilityId;
  const buildingReason = noBuilding ? 'Systems are connected per building: sign in as its Facility Admin.' : null;

  const run = async (fn: () => Promise<void>) => {
    setErr(null);
    try {
      await fn();
      changed();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  /** Open the connect dialog. With `where`, a reason it cannot shows at that control, not at the top. */
  const startConnect = (name: string, where?: string) => {
    const fail = (error: ApiError) => (where ? setPlaceErr({ where, error }) : setErr(error));
    setPlaceErr(null);
    if (!canWrite || noBuilding) {
      if (where) fail({ message: roReason ?? buildingReason ?? '' } as ApiError);
      return;
    }
    if (!catalog.data) {
      fail(catalog.error ?? ({ message: 'The catalog is still loading. Try again in a moment.' } as ApiError));
      return;
    }
    const item = findItem(name);
    if (!item) {
      fail({ message: `${name} is not in the integration catalog the server serves.` } as ApiError);
      return;
    }
    setCreds({});
    setConnect(item);
  };
  const askRemove = (name: string, setup: boolean) => {
    const conn = connectedOf(name);
    ask(setup
      ? { sec: 'Integrations', title: 'Remove ' + name + ' setup?', body: 'Residents connected through ' + name + ' stop sending readings. Their history stays.', btn: 'Remove setup',
          go: async () => { if (conn) await integrationRemove(client, { params: { id: conn.id }, query: { confirm: true } }); mine.reload(); devices.reload(); catalog.reload(); } }
      : { sec: 'Integrations', title: 'Disconnect ' + name + '?', body: 'Data from ' + name + ' stops arriving right away. Anything already received stays in the record.', btn: 'Disconnect',
          go: async () => { if (conn) await integrationRemove(client, { params: { id: conn.id }, query: { confirm: true } }); mine.reload(); } });
  };

  // What each partner needs before it can go live (Console V6 integrations NEED), shown on a
  // card that is not live and not connected.
  const NEED: Record<string, string> = {
    'Oracle Health (Cerner)': 'Oracle Health registers our app, then each building enables it.',
    athenahealth: 'athenahealth approves our Marketplace listing, then we add the keys.',
    'Quest Diagnostics': 'A lab interface agreement with Quest, or route results through Health Gorilla.',
    Labcorp: 'A lab interface agreement with Labcorp, or route results through Health Gorilla.',
    PointClickCare: 'PointClickCare Marketplace partner approval, then each building turns it on.',
    MatrixCare: 'Acceptance into the MatrixCare partner program.',
    Epic: 'Enrollment in Epic Vendor Services, a Showroom listing once one Epic customer is live, and a signed agreement with each health system.',
  };
  // ---- the catalog grid (Connect stack; Inbound and Outbound have no catalog) ----
  const cards = tab === 'Connect stack'
    ? stack.map((x) => {
        const on = !!connectedOf(x.name);
        const off = x.disabled === true;
        const pending = x.status === 'partner_pending';
        const notStarted = x.status === 'coming_soon';
        const logo = STACK_LOGO[x.id];
        return {
          x, on, off, name: x.name, state: STATE_WORD[x.status] ?? x.status.toUpperCase(),
          stateStyle: x.status === 'live' ? chip('#12503C', 'rgba(47,138,111,.16)') : pending ? chip('#5B6B7C', 'rgba(91,107,124,.18)') : chip('#54606C', 'rgba(34,39,46,.08)'),
          desc: x.blurb,
          need: !on && x.status !== 'live' ? NEED[x.name] ?? '' : '',
          mark: logo ? '' : INI[x.name] || x.name.replace(/[^A-Za-z ]/g, '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join(''),
          logo: logo
            ? "background-image:" + logoBg(logo) + ";background-size:contain;background-repeat:no-repeat;background-position:left center;width:68px;height:22px;flex:0 0 68px;display:block;"
            : 'display:flex;align-items:center;justify-content:center;width:68px;height:22px;flex:0 0 68px;border-radius:5px;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.02em;color:#54606C;background:rgba(34,39,46,.07);border:1px solid rgba(34,39,46,.13);',
          caps: x.caps ?? [PROTOCOL[x.protocol] ?? x.protocol],
          btn: off ? 'Disabled by your organization' : notStarted ? 'Not started' : on ? 'Connected' : pending ? 'Set up · pending approval' : 'Connect',
          btnStyle: (off ? btn('off') : notStarted ? btn('off') : on ? btn('soft') : pending ? btn('ghost') : btn('soft')) + 'width:100%;margin-top:10px;',
          wrap: 'box-sizing:border-box;border-radius:10px;padding:11px 13px;background:#FBF8F1;border:1px solid ' + (off ? 'rgba(34,39,46,.11);opacity:.55;' : on ? 'rgba(47,138,111,.34);' : 'rgba(34,39,46,.11);'),
        };
      })
    : [];
  const D = DEVICE_CATALOG;
  const dcShow = !!D && tab === 'Wearables';
  // One catalog entry per device row (Apple Watch and Apple Health share apple-health).
  const rowItem = (name: string) => (DEVICE_CATALOG_ID[name] ? all.find((x) => x.id === DEVICE_CATALOG_ID[name]) : findItem(name));
  const wearSetUp = new Map<string, boolean>();
  if (D) for (const g of D.groups) for (const d of g.devices) {
    const it = rowItem(d[0]);
    const key = it?.id ?? d[0];
    if (deviceSetUp(it?.name ?? d[0])) wearSetUp.set(key, it?.status === 'live');
  }
  const wearLive = [...wearSetUp.values()].filter(Boolean).length;
  const connCount = tab === 'Wearables' && D ? wearSetUp.size : cards.filter((c) => c.on).length;
  // Every catalog entry Aithora has switched off, wearables included (the banner shows on every tab).
  const hiddenN = all.filter((x) => x.disabled === true).length;
  const counts: Record<Tab, number | null> = { Inbound: hooks.data ? hooks.data.routes.length : null, Outbound: hooks.data ? hooks.data.routes.filter((r) => r.enabled).length : null, 'Connect stack': null, Wearables: null };

  const tone = (n: number) => (!D ? ['#54606C', ''] : n >= 85 ? ['#1F6B52', 'rgba(47,138,111,.16)'] : n >= 60 ? ['#2F5C97', 'rgba(79,138,214,.16)'] : n >= D.floor ? ['#54606C', 'rgba(34,39,46,.08)'] : ['#B3261E', 'rgba(179,38,30,.13)']);
  const setUpN = wearSetUp.size;
  const stepN = (done: boolean, on: boolean) =>
    'flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;font-size:16px;font-weight:800;' +
    (done ? 'background:#1F6B52;color:#fff;' : on ? 'background:#22272E;color:#FCFAF4;' : 'background:rgba(34,39,46,.08);color:#54606C;');
  const rule = (t: string, full: string, red?: boolean) => ({ t, full, style: 'font-size:16px;font-weight:700;white-space:nowrap;cursor:help;color:' + (red ? '#B3261E' : '#3C454F') + ';' });

  return (
    <div data-testid="settings-integrations">
      <div role="tablist" aria-label="Integrations" style={sx('display:flex;align-items:center;gap:7px;flex-wrap:wrap;margin-bottom:13px;flex-wrap:wrap;row-gap:8px;')}>
        {(['Inbound', 'Outbound', 'Connect stack', 'Wearables'] as Tab[]).map((n) => (
          <a key={n} href="#" role="tab" aria-selected={tab === n} onClick={(e) => { pre(e); setTab(n); }} style={sx(pill(tab === n))}>
            {I(n)}
            {counts[n] !== null && (
              <span style={sx('margin-left:7px;font-size:16px;font-weight:700;border-radius:4px;padding:1px 6px;' + (tab === n ? 'background:rgba(255,255,255,.24);color:#fff;' : 'background:rgba(34,39,46,.08);color:#54606C;'))}>{I(String(counts[n]))}</span>
            )}
          </a>
        ))}
        <span style={sx('flex:1;')} />
        <a href="#" {...wr(() => setHook({ name: '', url: '' }))} style={sx(btn(hookAdded ? 'soft' : 'primary'))}>+ Add webhook route</a>
      </div>
      {hook && (
        <div data-testid="settings-hook-form" style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:7px;margin:-4px 0 12px;')}>
          <input type="text" aria-label="Route name" placeholder="Name" value={hook.name} onChange={(e) => setHook({ ...hook, name: e.target.value })} style={sx('flex:1 1 140px;height:30px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;color:#22272E;box-sizing:border-box;')} />
          <input type="url" aria-label="Route URL" placeholder="https://" value={hook.url} onChange={(e) => setHook({ ...hook, url: e.target.value })} style={sx('flex:2 1 220px;height:30px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;color:#22272E;box-sizing:border-box;')} />
          <a href="#" {...wr(() => void run(async () => { await webhookRouteCreate(client, { body: { name: hook.name.trim(), forwardUrl: hook.url.trim() } }); setHook(null); setHookAdded(true); hooks.reload(); }))} style={sx(btn('primary'))}>Add route</a>
          <a href="#" onClick={(e) => { pre(e); setHook(null); }} style={sx(btn('ghost'))}>Cancel</a>
        </div>
      )}
      <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;margin-bottom:12px;')}>
        <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:9px;')}>
          <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
          <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Your connections</span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-size:16px;color:#54606C;')}>{I(tab === 'Wearables' && D ? connCount + ' set up · ' + wearLive + ' live' : connCount + ' connected')}</span>
        </div>
        <div style={sx('font-size:16px;color:#54606C;line-height:1.55;')}>
          {I(connCount === 0
            ? (tab === 'Wearables' ? 'No wearables set up yet. Register a device below: you enter its API keys once, then staff connect each resident from their profile.' : 'No systems connected yet. Pick one below to connect your stack.')
            : tab === 'Wearables' && D
              ? connCount + ' set up, ' + wearLive + ' live. Staff connect each resident from their own profile.'
              : connCount + (connCount === 1 ? ' connection is' : ' connections are') + ' live. Staff connect each resident from their own profile.')}
        </div>
      </div>
      <ErrLine error={err} style="margin:-4px 0 10px;" />
      <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;')}>
        <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;margin-bottom:11px;flex-wrap:wrap;row-gap:8px;')}>
          <span style={sx('width:7px;height:7px;border-radius:50%;background:#1F6B52;')} />
          <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>
            {I(tab === 'Wearables' ? 'Set up a device' : tab === 'Connect stack' ? 'Connect a system' : tab + ' routes')}
          </span>
          <span style={sx('flex:1;')} />
          <span style={sx('font-size:16px;color:#54606C;')}>{I(tab === 'Wearables' ? 'one-time API-key setup · per-resident connect lives on the resident' : 'bring your own credentials')}</span>
          {operatorTier && (
          <a
            href="#"
            aria-pressed={admin}
            onClick={(e) => { pre(e); setAdmin(!admin); }}
            style={sx('display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' + (admin ? 'background:rgba(79,138,214,.15);color:#2F5C97;border:1px solid rgba(47,92,151,.34);' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);'))}
          >
            {I(admin ? 'Done managing' : 'Manage catalog')}
          </a>
          )}
        </div>
        {manage && (
          <div style={sx('box-sizing:border-box;background:rgba(79,138,214,.08);border:1px solid rgba(47,92,151,.28);border-radius:9px;padding:10px 12px;margin-bottom:11px;')}>
            <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:5px;')}>
              <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.08em;font-weight:700;color:#2F5C97;')}>CATALOG ADMIN</span>
              <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
              <span style={sx('font-size:16px;color:#54606C;white-space:nowrap;')}>{I(hiddenN ? hiddenN + ' hidden from facilities' : 'all available')}</span>
            </div>
            <div style={sx('font-size:16px;color:#54606C;margin-top:5px;line-height:1.45;text-wrap:pretty;')}>
              {I('Disabling hides an integration from every facility - nobody can connect it and existing connections stop being offered. Use it for anything not actually working yet.')}
            </div>
          </div>
        )}
        {dcShow && D && (
          <>
            <div style={sx('display:flex;align-items:stretch;gap:0;flex-wrap:wrap;min-width:0;margin-bottom:10px;border-top:1px solid rgba(34,39,46,.10);border-bottom:1px solid rgba(34,39,46,.10);')}>
              {[
                { n: setUpN ? '✓' : '1', t: 'Add the API key', s: 'Once per vendor, here', numStyle: stepN(setUpN > 0, !setUpN) },
                { n: '2', t: 'Approve', s: 'Second admin confirms the key', numStyle: stepN(false, setUpN > 0) },
                { n: '3', t: 'Connect residents', s: 'From each resident’s profile', numStyle: stepN(false, false) },
              ].map((p) => (
                <span key={p.t} style={sx('flex:1 1 180px;min-width:0;display:flex;align-items:center;gap:9px;padding:8px 10px 8px 0;')}>
                  <span style={sx(p.numStyle)}>{I(p.n)}</span>
                  <span style={sx('display:flex;flex-direction:column;gap:1px;min-width:0;')}>
                    <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I(p.t)}</span>
                    <span style={sx('font-size:16px;color:#54606C;')}>{I(p.s)}</span>
                  </span>
                </span>
              ))}
            </div>
            <div style={sx('display:flex;align-items:baseline;gap:4px 18px;flex-wrap:wrap;min-width:0;margin-bottom:8px;')}>
              {[
                rule('Choose by the resident, not the brand', D.framing),
                rule('Grades 0–100 · below ' + D.floor + ' = context only', D.scaleNote),
                rule('Grades are ceilings', D.ceilingNote, true),
                rule('Only cuffs read blood pressure', D.bpRule, true),
              ].map((r) => (
                <span key={r.t} title={r.full} style={sx(r.style)}>{I(r.t)}</span>
              ))}
              <span style={sx('flex:1 1 10px;')} />
              <a href="#" aria-expanded={howOpen} onClick={(e) => { pre(e); setHowOpen(!howOpen); }} style={sx('display:inline-flex;align-items:center;min-height:32px;font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}>
                {I(howOpen ? 'Hide how grades work' : 'How grades work →')}
              </a>
            </div>
            {howOpen && (
              <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,300px),1fr));gap:6px 22px;margin-bottom:10px;padding:8px 0;border-top:1px solid rgba(34,39,46,.08);')}>
                <div style={sx('min-width:0;')}>
                  <div style={sx('font-size:16px;font-weight:700;')}>{I(D.framing)}</div>
                  <div style={sx('font-size:16px;color:#54606C;line-height:1.5;margin-top:4px;text-wrap:pretty;')}>
                    {I(D.scaleNote)}
                    {' '}
                    {I(D.ceilingNote)}
                  </div>
                  <div style={sx('font-size:16px;font-weight:700;margin-top:8px;')}>{I(D.trendOnly.title)}</div>
                  <div style={sx('font-size:16px;color:#54606C;line-height:1.5;margin-top:4px;text-wrap:pretty;')}>{I(D.trendOnly.body)}</div>
                </div>
                <div style={sx('min-width:0;')}>
                  <div style={sx('font-size:16px;font-weight:700;')}>{I(D.derate.title)}</div>
                  {D.derate.items.map((x) => (
                    <div key={x[0]} style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;padding:3px 0;border-top:1px solid rgba(34,39,46,.07);')}>
                      <span style={sx('flex:0 0 118px;font-size:16px;font-weight:700;')}>{I(x[0])}</span>
                      <span style={sx('flex:1 1 150px;min-width:0;font-size:16px;color:#54606C;line-height:1.45;')}>{I(x[1])}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {D.groups.map((g) => (
              <div key={g.g} style={sx('margin-bottom:8px;min-width:0;')}>
                <div style={sx('display:flex;align-items:baseline;gap:8px;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.14);')}>
                  <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#3C454F;')}>{I(g.g)}</span>
                  <span style={sx('font-size:16px;color:#54606C;')}>{I(g.devices.length + (g.devices.length === 1 ? ' device' : ' devices'))}</span>
                </div>
                <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,560px),1fr));column-gap:28px;min-width:0;')}>
                  {g.devices.map((d) => {
                    // The catalog entry behind this row, as the server serves it: its status is the
                    // badge, and it is what Aithora can switch off for every facility (as the
                    // Connect stack cards do). A switched-off device is off here too.
                    const item = rowItem(d[0]);
                    const cname = item?.name ?? d[0];
                    const missing = !!catalog.data && !item;
                    const off = item?.disabled === true;
                    const live = item?.status === 'live';
                    // Every device here that the catalog serves connects directly (its own adapter),
                    // so a device that is not live yet is not connected yet, never "via Junction".
                    const badge = !item ? (missing ? 'NOT CONNECTED YET' : '') : live ? 'CONNECTOR LIVE'
                      : item.status === 'partner_pending' ? STATE_WORD.partner_pending : 'NOT CONNECTED YET';
                    const setup = deviceSetUp(cname);
                    const conn = connectedOf(cname);
                    const testKey = item?.id ?? d[0];
                    const blocked = missing || off;
                    const why = off ? 'Aithora has turned this system off for now.' : missing ? NOT_IN_CATALOG : null;
                    const rowErr = placeErr?.where === d[0] ? placeErr.error : null;
                    const btnLabel = off ? 'Off' : missing ? 'Not in the catalog' : setup ? 'Set up ✓' : 'Set up';
                    const setUpNotLive = !!item && setup && !live && !off;
                    const testResult = tested[testKey] && tested[testKey] !== 'Testing…' ? tested[testKey] : null;
                    const note = setUpNotLive ? notLiveWhy(item) : null;
                    return (
                      <div key={d[0]} data-testid={'settings-device-' + d[0]} style={sx('display:flex;align-items:center;gap:4px 12px;flex-wrap:wrap;min-width:0;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.07);' + (off ? 'opacity:.55;' : ''))}>
                        {/* A fixed name column: the name wraps only between words, never inside one. */}
                        <span title={d[4]} style={sx('flex:0 0 176px;width:176px;min-width:0;display:flex;align-items:center;gap:9px;')}>
                          {d[1] && <span style={sx("background-image:" + logoBg(d[1]) + ";background-size:contain;background-repeat:no-repeat;background-position:left center;width:64px;height:20px;flex:0 0 64px;display:block;")} />}
                          <span data-name style={sx('flex:1 1 auto;min-width:0;font-size:16px;font-weight:700;line-height:1.25;white-space:normal;overflow-wrap:normal;word-break:normal;hyphens:none;')}>{I(d[0])}</span>
                        </span>
                        <span style={sx('order:3;flex:1 1 220px;min-width:0;display:flex;align-items:center;gap:3px 12px;flex-wrap:wrap;')}>
                          {d[3].map((m) => {
                            const gated = (m as unknown[])[2] === 'gated';
                            const t = gated ? ['#54606C', 'rgba(34,39,46,.08)'] : tone(m[1]);
                            return (
                              <span key={m[0]} title={m[0] + ' · grade ' + m[1] + (gated ? ' · not collected in v1' : m[1] < D.floor ? ' · below the floor: context only, never an alert' : '')} style={sx('display:inline-flex;align-items:center;gap:6px;white-space:nowrap;')}>
                                <span style={sx('font-size:16px;color:#3C454F;')}>{I(m[0] + (gated ? ' · not in v1' : ''))}</span>
                                <span style={sx('width:34px;height:4px;border-radius:2px;background:rgba(34,39,46,.09);display:inline-block;')}>
                                  <span style={sx('display:block;height:4px;border-radius:2px;width:' + m[1] + '%;background:' + t[0] + ';')} />
                                </span>
                                <span style={sx('font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;color:' + t[0] + ';')}>{I(String(m[1]))}</span>
                              </span>
                            );
                          })}
                        </span>
                        <span style={sx('order:1;flex:0 0 146px;width:146px;box-sizing:border-box;text-align:right;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;' + (live ? 'color:#1F6B52;' : 'color:#54606C;'))}>{I(setUpNotLive ? 'SET UP' : badge)}</span>
                        <a
                          href="#"
                          data-testid="settings-device-setup"
                          aria-disabled={blocked || !canWrite || noBuilding || undefined}
                          title={why ?? roReason ?? buildingReason ?? undefined}
                          onClick={(e) => {
                            pre(e);
                            if (why) { setPlaceErr({ where: d[0], error: { message: why } as ApiError }); return; }
                            if (setup && canWrite && !noBuilding) { setPlaceErr(null); askRemove(cname, true); }
                            else startConnect(cname, d[0]);
                          }}
                          style={sx('order:2;flex:0 0 140px;width:140px;box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;min-height:32px;padding:0 10px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (blocked ? 'color:#54606C;border:1px solid rgba(34,39,46,.14);' : setup ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : 'color:#fff;background:#2F5C97;'))}
                        >
                          {I(btnLabel)}
                        </a>
                        {/* A fixed slot, empty when there is nothing to test, so rows keep one layout. */}
                        <span style={sx('order:2;flex:0 0 112px;width:112px;display:inline-flex;align-items:center;min-height:32px;')}>
                        {conn && !off && (
                          // Reach the connected vendor once, as the Connect stack cards do.
                          <a
                            href="#"
                            {...wr(() => void run(async () => {
                              setTested((t) => ({ ...t, [testKey]: 'Testing…' }));
                              const r = await integrationTest(client, { params: { id: conn.id }, body: {} });
                              setTested((t) => ({ ...t, [testKey]: testLine(r) }));
                            }))}
                            style={sx('display:inline-flex;align-items:center;min-height:32px;font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}
                          >
                            {I(tested[testKey] === 'Testing…' ? 'Testing…' : 'Test connection')}
                          </a>
                        )}
                        </span>
                        {manage && (
                          <span style={sx('order:2;flex:0 0 76px;width:76px;display:inline-flex;align-items:center;')}>
                          {item && (
                          <a
                            href="#"
                            {...wr(() => void run(async () => { await integrationAvailability(client, { params: { id: item.id }, body: { disabled: !off } }); catalog.reload(); }))}
                            style={sx('flex:0 0 auto;display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' + (off ? 'background:rgba(47,138,111,.14);color:#1F6B52;border:1px solid rgba(47,138,111,.34);' : 'color:#B3261E;background:rgba(179,38,30,.1);border:1px solid rgba(179,38,30,.34);'))}
                          >
                            {I(off ? 'Enable' : 'Disable')}
                          </a>
                          )}
                          </span>
                        )}
                        {rowErr ? (
                          <span style={sx('order:4;flex:1 0 100%;min-width:0;')}>
                            <ErrLine error={rowErr} style="margin:0;" />
                          </span>
                        ) : (testResult || note) && (
                          <span role="status" style={sx('order:4;flex:1 0 100%;min-width:0;font-size:16px;line-height:1.45;color:' + (testResult && testResult.startsWith('Not reached') ? '#B3261E;' : '#54606C;'))}>
                            {I(testResult ?? note)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}
        {(tab === 'Inbound' || tab === 'Outbound') && (
          // Restored from the Flutter console (no design control): each webhook route, its
          // inbound address to copy, where it forwards, and switching it off or on.
          <div data-testid="settings-hook-routes" style={sx('min-width:0;margin-bottom:4px;')}>
            {tab === 'Outbound' && operatorTier && <OutboundAlerts />}
            {hooks.error && <ErrLine error={hooks.error} style="margin:0 0 8px;" />}
            {hooks.data && hooks.data.routes.length === 0 && (
              <div style={sx('font-size:16px;color:#54606C;line-height:1.55;')}>{I('No webhook routes yet. Add one: events sent to its inbound address pass the gate before they are forwarded.')}</div>
            )}
            {(hooks.data?.routes ?? []).map((r) => {
              const inbound = (typeof window !== 'undefined' ? window.location.origin : '') + WEBHOOK_INBOUND_PREFIX + r.inboundPath;
              return (
                <div key={r.id} style={sx('display:flex;align-items:center;gap:4px 12px;flex-wrap:wrap;min-width:0;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
                  <span style={sx('flex:0 1 160px;min-width:0;font-size:16px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;')}>{I(r.name)}</span>
                  <span title={tab === 'Inbound' ? inbound : r.forwardUrl} style={sx('flex:1 1 220px;min-width:0;font-variant-numeric:tabular-nums;font-size:16px;color:#3C454F;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;')}>
                    {I(tab === 'Inbound' ? inbound : '→ ' + r.forwardUrl)}
                  </span>
                  {tab === 'Outbound' && (
                    <span style={sx('font-size:16px;color:#54606C;white-space:nowrap;')}>{I((r.gated ?? 0) + ' held · ' + (r.blocked ?? 0) + ' refused since restart')}</span>
                  )}
                  <span style={sx(r.enabled ? chip('#12503C', 'rgba(47,138,111,.16)') : chip('#54606C', 'rgba(34,39,46,.08)'))}>{I(r.enabled ? 'ON' : 'OFF')}</span>
                  {tab === 'Inbound' && (
                    <a
                      href="#"
                      onClick={(e) => {
                        pre(e);
                        void navigator.clipboard?.writeText(inbound).catch(() => undefined);
                        setCopiedHook(r.id);
                      }}
                      style={sx('display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:6px;background:#FCFAF4;border:1px solid rgba(34,39,46,.16);font-size:14px;font-weight:700;letter-spacing:.06em;color:#3C454F;text-decoration:none;white-space:nowrap;')}
                    >
                      {I(copiedHook === r.id ? 'COPIED' : 'COPY')}
                    </a>
                  )}
                  <a
                    href="#"
                    role="switch"
                    aria-checked={r.enabled}
                    aria-label={r.name + ' enabled'}
                    {...wr(() => void run(async () => { await webhookRouteToggle(client, { params: { id: r.id }, body: { enabled: !r.enabled } }); hooks.reload(); }))}
                    style={sx('display:inline-flex;align-items:center;width:38px;height:22px;flex:0 0 38px;border-radius:13px;padding:2px;box-sizing:border-box;text-decoration:none;white-space:nowrap;' + (r.enabled ? 'background:#1F6B52;justify-content:flex-end;' : 'background:rgba(34,39,46,.22);justify-content:flex-start;'))}
                  >
                    <span style={sx('display:block;width:18px;height:18px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(34,39,46,.28);')} />
                  </a>
                </div>
              );
            })}
          </div>
        )}
        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:11px;' + (tab === 'Wearables' && D ? 'display:none;' : ''))}>
          {cards.map((c) => (
            <Fragment key={c.x.id}>
              <div style={sx(c.wrap)}>
                <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
                  <span style={sx(c.logo)}>{I(c.mark)}</span>
                  <span style={sx('font-size:16px;font-weight:700;color:#22272E;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{I(c.name)}</span>
                  <span style={sx('flex:1;')} />
                  <span style={sx(c.stateStyle)}>{I(c.state)}</span>
                </div>
                {c.desc && <div style={sx('font-size:16px;color:#54606C;line-height:1.5;margin-top:8px;')}>{I(c.desc)}</div>}
                {c.need && (
                  <div style={sx('font-size:16px;color:#22272E;line-height:1.45;margin-top:6px;')}>
                    <b>Needed:</b> {I(c.need)}
                  </div>
                )}
                <div style={sx('display:flex;flex-wrap:wrap;row-gap:6px;gap:5px;margin-top:9px;')}>
                  {c.caps.map((p) => (
                    <span key={p} style={sx('font-size:16px;color:#54606C;background:rgba(34,39,46,.05);border:1px solid rgba(34,39,46,.1);border-radius:5px;padding:2px 7px;')}>{I(p)}</span>
                  ))}
                </div>
                <a
                  href="#"
                  aria-disabled={c.off || c.btn === 'Not started' || !canWrite || noBuilding || undefined}
                  title={roReason ?? buildingReason ?? undefined}
                  onClick={(e) => {
                    pre(e);
                    if (c.off || c.btn === 'Not started' || !canWrite || noBuilding) return;
                    if (c.on) askRemove(c.name, false);
                    else startConnect(c.name);
                  }}
                  style={sx(c.btnStyle)}
                >
                  {I(c.btn)}
                </a>
                {c.on && (
                  // Restored from the Flutter console (no design control): reach the system once.
                  <a
                    href="#"
                    {...wr(() => void run(async () => {
                      const conn = connectedOf(c.name);
                      if (!conn) return;
                      setTested((t) => ({ ...t, [c.x.id]: 'Testing…' }));
                      const r = await integrationTest(client, { params: { id: conn.id }, body: {} });
                      setTested((t) => ({ ...t, [c.x.id]: testLine(r) }));
                    }))}
                    style={sx('display:inline-flex;align-items:center;min-height:24px;margin-top:6px;font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}
                  >
                    {I(tested[c.x.id] ?? 'Test connection')}
                  </a>
                )}
                {' '}
                {manage && (
                  <>
                    {' '}
                    <a
                      href="#"
                      {...wr(() => void run(async () => { await integrationAvailability(client, { params: { id: c.x.id }, body: { disabled: !c.off } }); catalog.reload(); }))}
                      style={sx('display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;margin-top:8px;' + (c.off ? 'background:rgba(47,138,111,.14);color:#1F6B52;border:1px solid rgba(47,138,111,.34);' : 'color:#B3261E;background:rgba(179,38,30,.1);border:1px solid rgba(179,38,30,.34);'))}
                    >
                      {I(c.off ? 'Enable' : 'Disable')}
                    </a>
                    {' '}
                  </>
                )}
              </div>
            </Fragment>
          ))}
        </div>
        {tab === 'Wearables' && (
          <div style={sx('box-sizing:border-box;border-radius:11px;padding:14px 16px;margin-top:12px;background:rgba(34,39,46,.04);border:1px solid rgba(34,39,46,.12);')}>
            <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:8px;')}>
              <span style={sx('font-size:16px;font-weight:700;')}>Don&rsquo;t see the device? Connect anything.</span>
              <span style={sx('flex:1;')} />
              <span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#264C7E;background:rgba(79,138,214,.16);border-radius:6px;padding:3px 9px;')}>300+ DEVICES</span>
            </div>
            <div style={sx('font-size:16px;color:#54606C;line-height:1.55;margin-top:8px;')}>
              One connection covers Oura, Fitbit, Garmin, Withings, Omron blood pressure, Dexcom CGM, Apple Health and hundreds more. Set it up once for the facility, then staff connect any resident from their profile.
            </div>
            <a
              href="#"
              aria-disabled={!canWrite || noBuilding || undefined}
              title={roReason ?? buildingReason ?? undefined}
              onClick={(e) => { pre(e); if (!deviceSetUp('Junction')) startConnect('Junction', 'junction'); }}
              style={sx(btn(deviceSetUp('Junction') ? 'soft' : 'primary') + 'width:100%;margin-top:12px;height:38px;')}
            >
              {I(deviceSetUp('Junction') ? 'Junction connected · 300+ devices available' : 'Set up Junction')}
            </a>
            <ErrLine error={placeErr?.where === 'junction' ? placeErr.error : null} />
          </div>
        )}
      </div>
      {connect && (
        <ConfirmDialog
          title={'Connect ' + connect.name}
          confirmLabel="Connect"
          body={
            <>
              <p>{connect.blurb}</p>
              {connect.fields.map((f) => (
                <label key={f.key} style={sx('display:block;margin-top:8px;font-size:16px;color:#3C454F;')}>
                  {f.label}
                  <input
                    type={f.secret ? 'password' : 'text'}
                    value={creds[f.key] ?? ''}
                    onChange={(e) => setCreds((p) => ({ ...p, [f.key]: e.target.value }))}
                    style={sx('display:block;width:100%;box-sizing:border-box;height:32px;margin-top:4px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;')}
                  />
                </label>
              ))}
            </>
          }
          onConfirm={async () => {
            await integrationCreate(client, { body: { catalogId: connect.id, ...creds } });
            mine.reload();
            devices.reload();
            // A facility's own app makes a direct wearable live for it: re-read the status.
            catalog.reload();
            changed();
          }}
          onClose={() => setConnect(null)}
        />
      )}
    </div>
  );
}
